import React, { useEffect, useState, useCallback } from "react";
import Menu from "./Menu/Menu";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";
import toast from "react-hot-toast";
import Loading from "../../../Utils/Loading/Loading";
import useSound from "use-sound";
import ErrorSoundFile from "../../../Sound/Error.wav";
import NotFound from "../../../Utils/NotFound/NotFound";
import TotalCard from "./TotalCard/TotalCard";
import PendingCard from "./PendingCard";
import moment from "moment";
import Filter from "./Filter/Filter";

const backend = await new Backend().init();

export default function Pending() {
  const userType = Number(localStorage.getItem("user_type") || 7);
  const [playError] = useSound(ErrorSoundFile);
  const windowSize = useSize();

  // --- Data States ---
  const [pending, setPending] = useState([]);
  const [pendingSearch, setPendingSearch] = useState([]);

  const [apending, setAPending] = useState(0);
  const [executed, setExecuted] = useState(0);
  const [rejected, setRejected] = useState(0);

  const [pendingDelete, setPendingDelete] = useState(false);

  // --- UI States ---
  const [loading, setLoading] = useState(false);

  const [isFetchedOnce, setIsFetchedOnce] = useState(false);

  // --- Filters ---
  const [userId, setUserId] = useState(0);
  const [masterId, setMasterId] = useState(0);
  const [superId, setSuperId] = useState(0);
  const [start, setStart] = useState(moment().startOf("week"));
  const [end, setEnd] = useState(moment().endOf("week"));

  // ✅ Centralized API Response Handler
  const handleTradeResponse = useCallback(
    (res) => {
      if (!res) return;

      if (res.error === "False") {
        setPending(res.pending || []);
        setPendingSearch(res.pending || []);

        setAPending(res.apending || 0);
        setExecuted(res.executed || 0);
        setRejected(res.rejected || 0);

        setPendingDelete(res.pending_delete || false);
      } else {
        if (res.logout === 1 && window.LogOut) {
          window.LogOut();
        } else {
          playError();
          toast.error(res.message || "Something went wrong");
        }
      }
    },
    [playError],
  );

  // ✅ Build payload dynamically based on user type
  const buildPayload = useCallback(
    (extraParams = {}) => {
      const base = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        start,
        end,
        ...extraParams,
      };

      switch (userType) {
        case 1:
          return {
            ...base,
            super_id: superId,
            master_id: masterId,
            user_id: userId,
          };
        case 2:
          return {
            ...base,
            super_id: localStorage.getItem("id"),
            master_id: masterId,
            user_id: userId,
          };
        case 3:
          return {
            ...base,
            master_id: localStorage.getItem("id"),
            user_id: userId,
          };
        case 4:
          return { ...base, user_id: localStorage.getItem("id") };
        case 5:
          return {
            ...base,
            broker_id: localStorage.getItem("id"),
            user_id: userId,
          };
        default:
          return base;
      }
    },
    [userType, start, end, userId, masterId, superId],
  );

  // ✅ Unified Fetch Function
  const fetchPending = useCallback(
    async (extraParams = {}) => {
      setLoading(true);
      try {
        const payload = buildPayload(extraParams);

        let res;
        switch (userType) {
          case 1:
            res = await backend.load_pending_admin(payload);
            break;
          case 2:
            res = await backend.load_pending_super(payload);
            break;
          case 3:
            res = await backend.load_pending_master(payload);
            break;
          case 4:
            res = await backend.pending(payload);
            break;
          case 5:
            res = await backend.load_pending_broker(payload);
            break;
          default:
            toast.error("Invalid user type");
            return;
        }

        handleTradeResponse(res);
      } catch (err) {
        playError();
        toast.error("Network error while loading trades");
        console.error("Trade fetch failed:", err);
      } finally {
        setLoading(false);
        setIsFetchedOnce(true);
      }
    },
    [userType, buildPayload, handleTradeResponse, playError],
  );

  // ✅ Initial Fetch
  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // ✅ Clear Filters
  const clearFilters = useCallback(() => {
    setUserId(0);
    setMasterId(0);
    setSuperId(0);
    fetchPending({ user_id: 0, master_id: 0, super_id: 0 });
  }, [fetchPending]);

  // ✅ Search
  const search = useCallback(
    (query) => {
      const q = query.trim().toLowerCase();
      setPending(
        q.length > 1
          ? pendingSearch.filter((t) => t.symbol?.toLowerCase().includes(q))
          : pendingSearch,
      );
    },
    [pendingSearch],
  );

  // ✅ Sort
  const handleSort = useCallback((sorted) => setPending(sorted), []);

  const handleFilter = useCallback(
    ({ super_id, master_id, user_id, start, end }) => {
      setSuperId(super_id || 0);
      setMasterId(master_id || 0);
      setUserId(user_id || 0);
      setStart(start);
      setEnd(end);

      fetchPending({
        super_id,
        master_id,
        user_id,
        start,
        end,
      });
    },
    [fetchPending],
  );

  return (
    <div>
      {/* --- Filter Section --- */}
      <Filter
        clear={clearFilters}
        search={search}
        records={pending.length}
        onFilter={handleFilter}
        userType={userType}
      />

      {/* --- Totals Section --- */}
      <TotalCard apending={apending} executed={executed} rejected={rejected} />

      {/* --- Menu --- */}
      <Menu
        userType={userType}
        pending_delete={pendingDelete}
        pending_search={pendingSearch}
        handlewSort={handleSort}
      />

      {/* --- Pending List --- */}
      <div
        style={{
          width: windowSize[0],
          height: windowSize[1] - 265,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Loading height={windowSize[1] - 265} />
        ) : pending.length > 0 ? (
          <div
            style={{
              width: windowSize[0],
              height: windowSize[1] - 265,
              overflowY: "auto",
            }}
          >
            {pending.map((item) => (
              <PendingCard
                key={item.id}
                item={item}
                reload={fetchPending}
                pending_delete={pendingDelete}
                userType={userType}
              />
            ))}
          </div>
        ) : isFetchedOnce ? (
          <NotFound
            height={windowSize[1] - 265}
            title={"Nothing here"}
            subtext={"No Pending Found."}
          />
        ) : null}
      </div>
    </div>
  );
}
